import { ChangeEvent, useState } from 'react';
import { message } from 'components/ui';

const extractDigits = (masked: string): string =>
  masked.replace(/\D/g, '').slice(1);

const PHONE_ERROR_TEXT: Record<'empty' | 'duplicate', string> = {
  empty: 'Заполните поле',
  duplicate: 'Такой номер телефона уже есть',
};

type UseAddPhoneFormParams = {
  isPhoneInList: (phoneDigits: string) => boolean;
  persistNewPhone: (phoneDigits: string) => Promise<any>;
};

export const useAddPhoneForm = ({
  isPhoneInList,
  persistNewPhone,
}: UseAddPhoneFormParams) => {
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [newPhone, setNewPhone] = useState<string>('');
  const [addPending, setAddPending] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<
    keyof typeof PHONE_ERROR_TEXT | null
  >(null);

  const digits = extractDigits(newPhone);
  const isPhoneComplete = digits.length === 10;
  const isDuplicate = isPhoneInList(digits);
  const canConfirm = isPhoneComplete && !isDuplicate;

  const openForm = () => setIsAdding(true);

  const changePhone = (e: ChangeEvent<HTMLInputElement>) => {
    setNewPhone(e.target.value);
    setPhoneError(null);
  };

  const clearPhone = () => {
    setNewPhone('');
    setPhoneError(null);
  };

  const confirmAddPhone = async () => {
    if (addPending) {
      return;
    }

    if (!isPhoneComplete) {
      setPhoneError('empty');

      return;
    }

    if (isDuplicate) {
      setPhoneError('duplicate');

      return;
    }

    setPhoneError(null);
    setAddPending(true);

    try {
      await persistNewPhone(digits);
      message({ type: 'success', text: 'Номер телефона добавлен' });
    } catch (error) {
      message({ type: 'error' });
    } finally {
      setAddPending(false);
      setNewPhone('');
      setIsAdding(false);
    }
  };

  return {
    isAdding,
    openForm,
    newPhone,
    changePhone,
    clearPhone,
    digits,
    addPending,
    canConfirm,
    confirmAddPhone,
    hasError: !!phoneError,
    phoneErrorText: phoneError ? PHONE_ERROR_TEXT[phoneError] : null,
  };
};
